import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Input, Select, Calendar, Button, Tabs, SubTabs, UploadButton } from '@baseV2'
import { TOAST_TYPE } from '@base'
import { strings } from '@constants'
import {
	getByPaymentId,
	getDueInvoices,
	downloadUrl,
	invoicePaymentAction,
	invoicesPaymentAction,
	attachInvoicesToPayment,
	getByDocumentNo,
} from '@data'
import { toggleDrawer } from '@data/state/action/root'
import { showLoader, showToast } from '@data/state/action'
import { displayAmount, showDateFormat, allowNumericKeyDown } from '@utils'
import { isFinanceAdmin, isSalesAdmin, isSupplyAdmin } from 'src/utils/adminFilter'
import { Spacings, Text } from '@styles'

import AttachInvoices from './AttachInvoices'
import InvoiceCard from './InvoiceCard'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	font-family: ${({ theme }) => theme.fontFamily.inter};
`
const TabsBar = styled.div`
	padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B} 0;
`
const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B};
	overflow-y: auto;
	flex: 1;
`
const Req = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`
const reqLabel = (text) => (
	<>
		{text} <Req>*</Req>
	</>
)
const SectionLabel = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.colors.text.secondary};
	margin-top: ${Spacings.SPACING_3B};
	padding-bottom: ${Spacings.SPACING_2B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	&::before {
		content: '';
		display: inline-block;
		width: 3px;
		height: 0.9rem;
		border-radius: 2px;
		background: ${({ theme }) => theme.colors.brand.primary};
	}
`
const DocList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
`
const DocRow = styled.a`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.brand.primary};
	text-decoration: none;
	word-break: break-all;

	&:hover {
		text-decoration: underline;
	}
`
const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	margin-top: ${Spacings.SPACING_3B};
`
const EmptyState = styled.div`
	padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
	text-align: center;
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
	border: 1px dashed ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	margin-top: ${Spacings.SPACING_3B};
`
const SummaryBar = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: ${Spacings.SPACING_2B};
	margin: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
	background: ${({ theme }) => theme.colors.surface.sunken};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const SummaryItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	padding: 0 ${Spacings.SPACING_2B};
	min-width: 0;
	border-right: 1px solid ${({ theme }) => theme.colors.border.default};

	&:last-child {
		border-right: none;
	}
`

const SummaryLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const SummaryValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme, $advance }) => ($advance ? theme.colors.brand.primary : theme.colors.text.primary)};
	max-width: 100%;
	text-align: center;
	overflow-wrap: anywhere;
`

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_4B};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
	flex-wrap: wrap;
`

const fmtDate = (val) => {
	if (!val) return ''
	const d = new Date(val)
	return Number.isNaN(d.getTime()) ? String(val) : showDateFormat(d)
}

// Normalize an invoice from the payment response (invoices list) into the
// shape InvoiceCard expects. `status` is the approval status (PENDING / APPROVED / REJECTED).
const normalizePaymentInvoice = (inv) => {
	const shipping = inv.shipping_address ?? inv.shipping_location ?? inv.delivery_address ?? inv.location
	const shippingLocation =
		typeof shipping === 'object' && shipping !== null
			? [shipping.complete_address, shipping.city, shipping.state, shipping.pincode].filter(Boolean).join(', ')
			: shipping || ''
	return {
		// The untouched API record — sent back in full by the attachInvoices payload.
		raw: inv,
		id: inv.transaction_no ?? inv.original_invoice_no,
		// Raw identifiers sent to the invoice-action API.
		transactionNo: inv.transaction_no ?? '',
		originalInvoiceNo: inv.original_invoice_no ?? '',
		number: inv.original_invoice_no ?? '--',
		invoiceAmount: Number(inv.invoice_amount ?? 0),
		taggedAmount: inv.amount != null ? Number(inv.amount) : null,
		balanceDue: inv.pending_amount != null ? Number(inv.pending_amount) : null,
		dueDateLabel: fmtDate(inv.invoice_date ?? inv.due_date),
		// Show the Zoho status.
		deliveryStatus: inv.zoho_status ?? inv.delivery_status ?? '',
		status: String(inv.status ?? 'PENDING').toUpperCase(),
		actionByLabel: inv.action_by?.full_name ?? (typeof inv.action_by === 'string' ? inv.action_by : ''),
		actionAtLabel: inv.action_at ? fmtDate(inv.action_at) : '',
		shippingLocation,
		docUrl: inv.document_url ?? inv.invoice_document ?? '',
	}
}

// A due invoice (getDueInvoices) → available-to-tag card shape. Same shape the Add Payment
// drawer builds, since both render the shared AttachInvoices cards.
const normalizeDueInvoice = (inv) => {
	const shipping = inv.shipping_address ?? inv.shipping_location ?? inv.delivery_address ?? inv.location ?? inv.address
	// Show only city, state, pincode (not the full address).
	const shippingLocation =
		typeof shipping === 'object' && shipping !== null
			? [shipping.city, shipping.state, shipping.pincode].filter(Boolean).join(', ')
			: shipping || ''
	return {
		// The untouched API record — sent back in full by the attachInvoices payload.
		raw: inv,
		id: inv.transaction_no ?? inv.original_invoice_no,
		transactionNo: inv.transaction_no ?? '',
		number: inv.original_invoice_no ?? '--',
		invoiceAmount: Number(inv.invoice_amount ?? 0),
		taggedAmount: null,
		balanceDue: inv.pending_amount != null ? Number(inv.pending_amount) : null,
		dueDateLabel: fmtDate(inv.invoice_date ?? inv.due_date),
		// Show the Zoho status.
		deliveryStatus: inv.zoho_status ?? inv.delivery_status ?? '',
		status: 'UNTAGGED',
		shippingLocation,
		docUrl: inv.invoice_document ?? inv.document_url ?? '',
	}
}

const handleAmount = (raw) => {
	if (raw === '') return ''
	const cleaned = raw.replace(/[^\d.]/g, '')
	const [intRaw, ...dec] = cleaned.split('.')
	const intPart = intRaw.slice(0, 12)
	const decPart = dec.length > 0 ? `.${dec.join('').slice(0, 2)}` : ''
	return intPart + decPart
}

const INVOICE_SUBTABS = [
	{ id: 'tagged', label: 'Tagged' },
	{ id: 'untagged', label: 'Untagged' },
	{ id: 'approved', label: 'Approved' },
	{ id: 'rejected', label: 'Rejected' },
]

const EditPayment = ({ paymentId, update, documentNumber }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	// Only the commodities assigned to the logged-in user.
	const commodities = useMemo(() => user?.commodity ?? [], [user?.commodity])
	// Only admins and sales admins can edit a tagged amount; everyone else sees it read-only.
	// Finance admins review payments only — no tagging, no amount edits, no Update Payment.
	const isFinance = isFinanceAdmin(user?.role)
	const canEditAmount = isSalesAdmin(user?.role) && !isFinance
	// Finance admins can view every tab, but can't submit an Update Payment.
	const canUpdatePayment = !isFinance
	// Sales and supply admins can't approve/reject invoices.
	const canApproveReject = !isSalesAdmin(user?.role, false) && !isSupplyAdmin(user?.role, false)

	const [payment, setPayment] = useState(null)
	const [tab, setTab] = useState('detail') // detail | invoice
	const [invoiceTab, setInvoiceTab] = useState('tagged')

	// Detail form
	const [form, setForm] = useState({ organisation: null, account: null, commodity: null, remark: '', amount: '', paymentDate: null })
	const [newDocuments, setNewDocuments] = useState([]) // File[] (newly added)

	// Invoice state: tagged = the payment.invoices list (bucketed by their `status`), available = due invoices.
	const [tagged, setTagged] = useState([]) // invoices → Tagged / Approved / Rejected tabs (by status)
	const [dueInvoices, setDueInvoices] = useState([]) // getDueInvoices (full list for the org)
	// Amounts the user is tagging to untagged (due) invoices, keyed by invoice id.
	const [allocations, setAllocations] = useState({})
	const [allocErrors, setAllocErrors] = useState({})
	// Tagged amounts the admin has edited on the mapped cards, keyed by invoice id.
	const [taggedEdits, setTaggedEdits] = useState({})

	const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

	// Load the payment + supporting lists.
	useEffect(() => {
		const featchApi = paymentId ? getByPaymentId(paymentId) : getByDocumentNo(documentNumber)
		dispatch(showLoader(true))
		featchApi
			.then((res) => {
				const data = paymentId ? (res?.payment_id ? res : (res?.data ?? res)) : res?.payment
				setPayment(data)
				setForm((prev) => ({
					...prev,
					remark: data?.transaction_remarks ?? '',
					amount: data?.amount != null ? String(data.amount) : '',
					paymentDate: data?.payment_date ?? null,
					commodity: data?.commodity ?? null,
					// Organisation and account are read-only here, so take them straight off the
					// payment — no need to fetch the full clients / accounts lists to match against.
					organisation: data?.user_details
						? {
								...data.user_details,
								display_name: data.user_details.organisation_name || data.user_details.organisation_id,
							}
						: null,
					account: data?.mbook_bank
						? {
								...data.mbook_bank,
								display_label: data.mbook_bank.name || data.mbook_bank.bank_name || data.mbook_bank.account_number || '--',
							}
						: null,
				}))
				// All Invoice-tab buckets come from the payment.invoices list, split by each invoice's `status`.
				setTagged((data?.invoices ?? data?.active_invoices ?? []).map(normalizePaymentInvoice))
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message || error?.data?.message || strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}, [paymentId, documentNumber, dispatch])

	// The org's due invoices. They carry the details the payment's own invoice rows don't
	// (document, Zoho status, shipping address), so they also enrich the mapped cards below.
	useEffect(() => {
		const orgId = payment?.user_details?.organisation_id
		if (!orgId) return
		getDueInvoices(orgId)
			.then((res) => setDueInvoices((res ?? []).map(normalizeDueInvoice)))
			.catch(() => setDueInvoices([]))
	}, [payment])

	// Due invoices not already tagged to this payment → Untagged tab.
	const available = useMemo(() => {
		const takenNos = new Set(tagged.map((t) => t.number))
		return dueInvoices.filter((inv) => !takenNos.has(inv.number))
	}, [dueInvoices, tagged])

	// Fill in the fields the payment's invoice rows don't return, from the matching due invoice.
	const taggedInvoices = useMemo(() => {
		if (dueInvoices.length === 0) return tagged
		const byNumber = new Map(dueInvoices.map((inv) => [inv.number, inv]))
		return tagged.map((inv) => {
			const due = byNumber.get(inv.number)
			if (!due) return inv
			return {
				...inv,
				docUrl: inv.docUrl || due.docUrl,
				deliveryStatus: inv.deliveryStatus || due.deliveryStatus,
				shippingLocation: inv.shippingLocation || due.shippingLocation,
			}
		})
	}, [tagged, dueInvoices])

	const close = () => dispatch(toggleDrawer(false))

	const existingDocuments = payment?.documents ?? []

	// --- invoice bucket helpers ---
	// Update an invoice's status in place → it moves between the Tagged / Approved / Rejected tabs.
	const setStatus = (id, status) => setTagged((prev) => prev.map((inv) => (inv.id === id ? { ...inv, status } : inv)))

	// Approve / Reject a single tagged invoice via the invoice-action API.
	//   action = APPROVED | REJECTED (caps), actionBy = logged-in user id,
	//   originalInvoiceNo = invoice's original_invoice_no, paymentId = the payment's payment_id.
	const invoiceActionApi = (inv, action) =>
		invoicePaymentAction({
			action,
			actionBy: user?.user_id,
			originalInvoiceNo: inv.originalInvoiceNo || inv.number,
			paymentId: payment?.payment_id ?? paymentId,
		})

	const handleInvoiceAction = (inv, action) => {
		dispatch(showLoader(true))
		invoiceActionApi(inv, action)
			.then(() => {
				setStatus(inv.id, action)
				dispatch(showToast(true, `Invoice ${action === 'APPROVED' ? 'approved' : 'rejected'} successfully`, { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => dispatch(showToast(true, err?.response?.data?.message || 'Action failed', { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
	}

	// Bulk approve/reject ALL pending invoices on the payment via the bulk invoices-action API.
	const bulkAction = (action) => {
		const hasPending = tagged.some((inv) => inv.status === 'PENDING')
		if (!hasPending) return
		dispatch(showLoader(true))
		invoicesPaymentAction({ action, actionBy: user?.user_id, paymentId: payment?.payment_id ?? paymentId })
			.then(() => {
				setTagged((prev) => prev.map((inv) => (inv.status === 'PENDING' ? { ...inv, status: action } : inv)))
				dispatch(showToast(true, `Invoices ${action === 'APPROVED' ? 'approved' : 'rejected'}`, { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => dispatch(showToast(true, err?.response?.data?.message || 'Action failed', { type: TOAST_TYPE.ERROR })))
			.finally(() => dispatch(showLoader(false)))
	}
	const approveAll = () => bulkAction('APPROVED')
	const rejectAll = () => bulkAction('REJECTED')

	// Payment totals: Total = amount, Attached = already-tagged amounts + amounts being tagged now.
	const total = Number(form.amount) || 0
	const taggedAmount = useMemo(() => tagged.reduce((sum, inv) => sum + (Number(inv.taggedAmount) || 0), 0), [tagged])
	const newlyAllocated = useMemo(() => Object.values(allocations).reduce((sum, v) => sum + (Number(v) || 0), 0), [allocations])
	const attached = taggedAmount + newlyAllocated
	const advance = Math.max(0, total - attached)

	// Tag an amount against an untagged invoice: clamp to its balance due AND to the payment's
	// remaining advance, so Attached never exceeds Total (same rules as the Add Payment drawer).
	const handleAllocation = (invoice, rawValue) => {
		if (rawValue === '') {
			setAllocations((a) => ({ ...a, [invoice.id]: '' }))
			setAllocErrors((e) => ({ ...e, [invoice.id]: undefined }))
			return
		}
		let val = Number(rawValue)
		if (Number.isNaN(val) || val < 0) val = 0

		if (!(invoice.balanceDue > 0)) {
			setAllocErrors((e) => ({ ...e, [invoice.id]: 'No pending amount on this invoice' }))
			setAllocations((a) => ({ ...a, [invoice.id]: '' }))
			return
		}

		setAllocations((a) => {
			const others = Object.entries(a).reduce((sum, [id, v]) => (id === String(invoice.id) ? sum : sum + (Number(v) || 0)), 0)
			const budgetLeft = Math.max(0, total - taggedAmount - others)
			const cap = Math.min(invoice.balanceDue, budgetLeft)

			if (total <= 0) {
				setAllocErrors((e) => ({ ...e, [invoice.id]: 'Enter payment amount first' }))
				return a
			}
			if (cap <= 0) {
				setAllocErrors((e) => ({ ...e, [invoice.id]: 'Payment amount fully allocated' }))
				return a
			}

			const clamped = Math.round(Math.min(val, cap) * 100) / 100
			setAllocErrors((e) => ({ ...e, [invoice.id]: undefined }))
			return { ...a, [invoice.id]: clamped }
		})
	}

	// Update Payment -> PUT /invoice-payment/attachInvoices?paymentId=...
	// Sends ONLY what the user changed in this session — untouched invoices already tagged to the
	// payment are left out of the payload entirely.
	const attachPayload = useMemo(() => {
		// Tagged cards: include an invoice only when its amount was actually edited to a new value.
		const fromTagged = taggedInvoices
			.filter((inv) => {
				const edited = taggedEdits[inv.id]
				if (edited === undefined || edited === '') return false
				return Number(edited) !== Number(inv.taggedAmount ?? 0)
			})
			// Send the COMPLETE invoice object back, with the edited amount applied on top.
			.map((inv) => ({
				...inv.raw,
				original_invoice_no: inv.originalInvoiceNo || inv.number,
				amount: Number(taggedEdits[inv.id]),
				pending_amount: inv.balanceDue,
			}))

		// Untagged cards: include the invoices the user allocated an amount against just now.
		const fromUntagged = available
			.filter((inv) => Number(allocations[inv.id]) > 0)
			.map((inv) => ({
				...inv.raw,
				original_invoice_no: inv.number,
				amount: Number(allocations[inv.id]),
				pending_amount: inv.balanceDue,
			}))

		return [...fromTagged, ...fromUntagged]
	}, [taggedInvoices, taggedEdits, available, allocations])

	const updatePaymentHandler = () => {
		if (attachPayload.length === 0) {
			dispatch(showToast(true, 'No changes to update', { type: TOAST_TYPE.ERROR }))
			return
		}
		if (attached > total) {
			dispatch(showToast(true, 'Attached amount cannot exceed the payment amount', { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(showLoader(true))
		attachInvoicesToPayment({ invoices: attachPayload, paymentId: payment?.payment_id ?? paymentId })
			.then(() => {
				dispatch(showToast(true, 'Invoices updated successfully', { type: TOAST_TYPE.SUCCESS }))
				update?.()
				close()
			})
			.catch((err) => {
				const msg = err?.response?.data?.message || err?.message || 'Failed to update invoices'
				dispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	// All buckets from the payment.invoices list, split by each invoice's `status`.
	const buckets = useMemo(
		() => ({
			tagged: taggedInvoices.filter((inv) => inv.status === 'PENDING'),
			approved: taggedInvoices.filter((inv) => inv.status === 'APPROVED'),
			rejected: taggedInvoices.filter((inv) => inv.status === 'REJECTED'),
			untagged: available,
		}),
		[taggedInvoices, available]
	)

	// A mapped invoice card: Approve/Reject while pending, else a status badge.
	// Shared by the Detail tab's "Attach Invoice" section and the Invoice tab's tagged/approved/rejected lists.
	const renderMappedCard = (inv) => {
		let statusBadge
		let actions = []
		if (inv.status === 'APPROVED') {
			statusBadge = { label: 'Approved', color: 'success' }
		} else if (inv.status === 'REJECTED') {
			statusBadge = { label: 'Rejected', color: 'error' }
		} else if (canApproveReject) {
			actions = [
				{ label: 'Approve', color: 'success', onClick: () => handleInvoiceAction(inv, 'APPROVED') },
				{ label: 'Reject', color: 'danger', variant: 'outline', onClick: () => handleInvoiceAction(inv, 'REJECTED') },
			]
		}
		const isPending = inv.status === 'PENDING'
		return (
			<InvoiceCard
				key={inv.id}
				invoice={inv}
				statusBadge={statusBadge}
				actions={actions}
				active={isPending}
				showAmountInput={isPending}
				amountValue={taggedEdits[inv.id] ?? inv.taggedAmount ?? ''}
				amountDisabled={!canEditAmount}
				onAmountChange={(invoice, value) => setTaggedEdits((prev) => ({ ...prev, [invoice.id]: value }))}
			/>
		)
	}

	const renderInvoiceList = () => {
		// Untagged (due) invoices reuse the Add Payment cards, so amounts can be tagged right here.
		if (invoiceTab === 'untagged') {
			return (
				<AttachInvoices
					invoices={available}
					allocations={allocations}
					allocErrors={allocErrors}
					attached={attached}
					advance={advance}
					onAllocationChange={handleAllocation}
					disabled={!canUpdatePayment}
				/>
			)
		}

		const list = buckets[invoiceTab] ?? []
		if (list.length === 0) return <EmptyState>No invoices in this list</EmptyState>

		return <List>{list.map(renderMappedCard)}</List>
	}

	return (
		<Wrapper>
			<TabsBar>
				<Tabs
					data={[
						{ label: 'Detail', selected: tab === 'detail', onClick: () => setTab('detail') },
						{ label: 'Invoices', selected: tab === 'invoice', onClick: () => setTab('invoice') },
					]}
				/>
			</TabsBar>

			{tab === 'detail' ? (
				<Body>
					<SectionLabel>{strings('organisation')}</SectionLabel>
					<Select
						label={reqLabel(strings('select', 'organisation'))}
						placeholder={strings('select', 'organisation')}
						data={form.organisation ? [form.organisation] : []}
						displayKey='display_name'
						primaryKey='organisation_id'
						value={form.organisation ?? {}}
						onChange={(org) => setField('organisation', org)}
						disabled
					/>
					<Select
						label={reqLabel(strings('select', 'account'))}
						placeholder={strings('select account')}
						data={form.account ? [form.account] : []}
						displayKey='display_label'
						primaryKey='account_number'
						value={form.account ?? {}}
						onChange={(acc) => setField('account', acc)}
						disabled
					/>
					<Select
						label={reqLabel(strings('commodity'))}
						placeholder={strings('select', 'commodity')}
						data={commodities}
						displayKey='commodity_name'
						primaryKey='commodity_id'
						value={commodities.find((item) => item?.commodity_id === form?.commodity?.commodity_id) ?? form?.commodity ?? {}}
						onChange={(item) => setField('commodity', item)}
						disabled
					/>

					<SectionLabel>{strings('payment', 'details')}</SectionLabel>
					<Input
						label={reqLabel(strings('transaction', 'remark'))}
						placeholder={strings('enter transaction remark')}
						value={form.remark}
						onChange={(e) => setField('remark', e.target.value)}
						// The only editable field in this drawer — and finance admins are view-only.
						disabled={!canUpdatePayment}
					/>
					<Input
						type='number'
						min='0'
						label={reqLabel(strings('amount'))}
						placeholder={strings('enter payment amount')}
						value={form.amount}
						onKeyDown={allowNumericKeyDown}
						onChange={(e) => setField('amount', handleAmount(e.target.value))}
						disabled
					/>
					{/* Everything except the transaction remark is read-only while editing a payment. */}
					<Calendar
						label={strings('payment', 'date')}
						value={form.paymentDate}
						onChange={(val) => setField('paymentDate', val)}
						maxDate={new Date()}
						disableFutureMonth
						disabled
					/>

					<SectionLabel>{strings('upload', 'documents')}</SectionLabel>
					{existingDocuments.length > 0 && (
						<DocList>
							{existingDocuments.map((doc, idx) => (
								<DocRow key={`${doc}-${idx}`} href={downloadUrl + doc} target='_blank' rel='noreferrer'>
									{doc.split('/').pop()}
								</DocRow>
							))}
						</DocList>
					)}
					{/* Documents can't be changed from the edit drawer — view only. */}
					<UploadButton files={newDocuments} onChange={setNewDocuments} disabled />
				</Body>
			) : (
				<Body>
					<SubTabs tabs={INVOICE_SUBTABS} activeTab={invoiceTab} onChange={setInvoiceTab} />
					{renderInvoiceList()}
				</Body>
			)}

			<SummaryBar>
				<SummaryItem>
					<SummaryLabel>{strings('total')}</SummaryLabel>
					<SummaryValue>{displayAmount(total)}</SummaryValue>
				</SummaryItem>
				<SummaryItem>
					<SummaryLabel>Attached</SummaryLabel>
					<SummaryValue>{displayAmount(attached)}</SummaryValue>
				</SummaryItem>
				<SummaryItem>
					<SummaryLabel>{strings('advance')}</SummaryLabel>
					<SummaryValue $advance>{displayAmount(advance)}</SummaryValue>
				</SummaryItem>
			</SummaryBar>

			<Footer>
				<Button variant='outline' color='neutral' onClick={close}>
					{strings('cancel')}
				</Button>
				{canApproveReject && !(tab === 'invoice' && invoiceTab === 'untagged') && (
					<>
						<Button variant='outline' color='success' onClick={approveAll} disabled={buckets.tagged.length === 0}>
							Approve All
						</Button>
						<Button variant='outline' color='danger' onClick={rejectAll} disabled={buckets.tagged.length === 0}>
							Reject All
						</Button>
					</>
				)}
				{canUpdatePayment && tab === 'invoice' && (invoiceTab === 'tagged' || invoiceTab === 'untagged') && (
					<Button color='primary' onClick={updatePaymentHandler}>
						Update Payment
					</Button>
				)}
			</Footer>
		</Wrapper>
	)
}

export default EditPayment
